import { useQuery } from '@apollo/client';
import { useParams } from 'react-router';

import Button from '@/componentsv2/Button';
import { hasErrorCode } from '@/core/api';
import ERROR_CODES from '@/core/apiErrorCodes';

import EngagementHubLive from './EngagementHubLive';
import { ENGAGEMENT_HUB } from './operations';
import { useSessionViewer, useViewerProfileId } from './session';
import { fromLiveEngagement } from './viewModel';

/**
 * `/engagements/:id` — the live Engagement Hub.
 *
 * The `consulting-engagements` flag gate is one level up in `EngagementRoute`;
 * this component only renders when the feature is on.
 *
 * State contract (plan §5):
 *  - query in flight        → skeleton, no layout shift
 *  - transient query error  → retry panel. `engagement.not_found` is explicitly
 *    *not* transient and must be excluded, or a bad link offers a Retry that
 *    can never succeed
 *  - engagement.not_found   → the same "not available" screen as a null below
 *  - null engagement        → "not available"; permission-denied and not-found
 *    are indistinguishable **by api design**, so the copy never says "you lack
 *    permission" — that sentence is itself the information leak the api avoids
 *  - field-level nulls      → the panels hide their own blocks; not an error
 */
export default function EngagementPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const viewer = useSessionViewer();
  const viewerProfileId = useViewerProfileId();
  const { data, loading, error, refetch } = useQuery(ENGAGEMENT_HUB, {
    variables: { id: id ?? '' },
    skip: !id,
  });

  if (loading) return <HubSkeleton />;

  // A missing — or not-permitted — engagement arrives as `engagement: null`
  // *plus* a GraphQL error, so Apollo's default errorPolicy makes `error`
  // truthy and the retry branch below would win. That offered a Retry that can
  // never succeed, and it left the neutral "isn't available" copy unreachable.
  //
  // TWO codes reach here, not one. `engagement.not_found` comes from
  // FindEngagement, but `permission.Require` runs BEFORE it in the api's
  // engagementQuery resolver, so a non-participant gets `permission.not_allowed`
  // instead — and that is the common case, since a denial is what a shared or
  // stale engagement link produces. Excluding only the first code is the
  // ENG-5605 AC6 failure.
  if (
    error &&
    !hasErrorCode(error, ERROR_CODES.ENGAGEMENT_NOT_FOUND) &&
    !hasErrorCode(error, ERROR_CODES.PERMISSION_NOT_ALLOWED)
  ) {
    return (
      <Centered>
        <h1 className="text-on-surface hd-5">We couldn’t load this engagement.</h1>
        <p className="mt-8 text-on-surface-muted body-2">
          The connection may have dropped. Try again in a moment.
        </p>
        <div className="mt-16">
          <Button onClick={() => void refetch()}>Retry</Button>
        </div>
      </Centered>
    );
  }

  if (!data?.engagement) {
    return (
      <Centered>
        <h1 className="text-on-surface hd-5">This engagement isn’t available.</h1>
        <p className="mt-8 text-on-surface-muted body-2">
          It may have been removed, or the link may be wrong.
        </p>
      </Centered>
    );
  }

  // Keyed by engagement. The route reuses this element for every id and
  // ENGAGEMENT_HUB is cache-first, so moving to an engagement already in the
  // cache re-rendered the Hub instead of remounting it. A half-written
  // governance rejection, the open tab and the ActionStrip's expanders all
  // carried over, one click from sending on the wrong engagement (ENG-5752).
  return (
    <EngagementHubLive
      key={data.engagement.id}
      view={fromLiveEngagement(data.engagement)}
      viewer={viewer}
      viewerProfileId={viewerProfileId}
    />
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto flex max-w-[1200px] flex-col p-24 md:p-32">
      <div className="rounded-lg border border-surface-border bg-surface-card p-24">{children}</div>
    </div>
  );
}

/** Header + tab strip in outline, so the real content doesn't shift on arrival. */
function HubSkeleton(): JSX.Element {
  return (
    <div
      className="mx-auto flex max-w-[1200px] animate-pulse flex-col gap-24 p-24 md:p-32"
      aria-busy="true"
      aria-label="Loading engagement"
    >
      <div className="flex flex-col gap-16 border-b border-surface-border pb-24">
        <div className="h-12 w-1/4 rounded-sm bg-surface-secondary" />
        <div className="h-32 w-1/2 rounded-sm bg-surface-secondary" />
        <div className="grid grid-cols-1 gap-24 md:grid-cols-3">
          <div className="h-48 rounded-sm bg-surface-secondary" />
          <div className="h-48 rounded-sm bg-surface-secondary" />
          <div className="h-48 rounded-sm bg-surface-secondary" />
        </div>
      </div>
      <div className="h-32 w-1/3 rounded-sm bg-surface-secondary" />
      <div className="h-200 rounded-lg bg-surface-secondary" />
    </div>
  );
}
